import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import debtSecurityService from "@debtSecurityService";

export function useFetchDebtSecurities() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['debt_securities', user?.id],
    queryFn: async () => {
      return await debtSecurityService.fetchDebtSecurities(user.id);
    },
    enabled: !!user,
  });
}

export function useCheckISINExists(isin: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['check_isin', isin, user?.id],
    queryFn: async () => {
      return await debtSecurityService.checkISINExists({ isin, userId: user!.id });
    },
    enabled: !!user && !!isin && isin.length === 12,
  });
}

export function useCreateDebtSecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (security: Omit<DebtSecurityCreateRequest, 'userId'>) => {
      return await debtSecurityService.createDebtSecurity({...security, userId: user.id });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debt_securities'] });
      qc.invalidateQueries({ queryKey: ['check_isin'] });
    },
  });
}

export function useUpdateDebtSecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (req: Omit<DebtSecurityUpdateRequest, 'userId'>) => {
      return await debtSecurityService.updateDebtSecurity({...req, userId: user.id});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debt_securities'] });
      qc.invalidateQueries({ queryKey: ['debt_security_detail'] });
    },
  });
}

export function useDeleteDebtSecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      return await debtSecurityService.deleteDebtSecurity({ id, userId: user!.id});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debt_securities'] }),
  });
}

export function useFetchDebtSecurityDetail(id: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['debt_security_detail', id, user?.id],
    queryFn: async () => {
      return await debtSecurityService.fetchDebtSecurityById({
        id,
        userId: user!.id,
      });
    },
    enabled: !!user && !!id,
  });
}
